import { useEffect, useState } from 'react';
import { careerAPI } from '../services/careerApi';
import { CareerPosting } from '../types/career';
import Breadcrumb from '../components/layout/Breadcrumb';
import Card from '../components/ui/Card';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import ErrorState from '../components/ui/ErrorState';
import { usePageTitle } from '../hooks/usePageTitle';

export default function Careers() {
  usePageTitle('Careers');
  const [postings, setPostings] = useState<CareerPosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = () => {
    setLoading(true);
    careerAPI
      .fetchPostings()
      .then((rows) => {
        setPostings(rows);
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err : new Error('Failed to load careers')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Careers' }]} />
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Careers</h1>
      {!loading && !error && postings.length === 0 ? (
        <p className="text-gray-700 mb-8">
          Interested in working with ORBCoN? We don’t have any openings right now. Please check back later.
        </p>
      ) : (
        <p className="text-gray-700 mb-8">
          Interested in working with ORBCoN? Current openings are listed below.
        </p>
      )}
      {loading && <LoadingSkeleton lines={4} />}
      {error && <ErrorState error={error} title="Could not load careers" onRetry={load} />}
      {!loading && postings.length > 0 && (
        <div className="space-y-4">
          {postings.map((posting) => (
            <Card key={posting.id} className="!p-6">
              <h2 className="text-xl font-semibold text-gray-900">{posting.title}</h2>
              <p className="text-gray-600 mt-1">{posting.sponsor}</p>
              {posting.location && <p className="text-sm text-gray-500 mt-1">{posting.location}</p>}
              {posting.summary && <p className="text-gray-700 mt-4 whitespace-pre-wrap">{posting.summary}</p>}
              <a
                href={posting.applyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center mt-6 px-6 py-3 rounded-md font-medium bg-primary text-white hover:bg-primary-dark"
              >
                Apply
              </a>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
